import React, { useEffect, useState } from "react";
import WeeklyCalendar from "../../../../molecules/Calendar/WeeklyCalendar";

import notificationStore from "../../../../stores/Notification/notificationStore";
import { WeeklyWorkDayDto, WorkPlanDto } from "../../../../api-client";
import { Alert, Box, Button, CircularProgress, Grid, Typography } from "@mui/material";

import { Event } from "../../../../molecules/Calendar/Calendar.type";
import moment, * as Moments from "moment";

import { extendMoment } from "moment-range";
import WeeklyWorkDays from "./WeeklyWorkDays";
import {
  MergedWeeklyWorkDay,
  PENDING_COLOR,
  PendingWeeklyWorkDayChange,
  createPendingId,
  formatWorkDayTime,
  isPendingId,
  mergePendingWorkDays,
} from "./pendingWorkDays";
import WorkPlans from "./WorkPlans";
import EffectiveDateDialog from "./EffectiveDateDialog";
import PendingChangeBanner from "./PendingChangeBanner";
import { DateSelectArg } from "@fullcalendar/core";
import { mobiliTheme } from "../../../../themes/mobiliTheme";
import { TopLevelPaper } from "../../../../themes/StyledComponents";
import { useWeeklyWorkDaysByWorkPlan, useWorkPlans } from "../../../../stores/User/user.queries";
import { useCreateWeeklyWorkDay, useUpdateWeeklyWorkDay, useVersionWorkPlan } from "../../../../stores/User/user.mutations";
import { useUnsavedChanges } from "../UnsavedChangesGuard";
const momentRange = extendMoment(Moments);
interface WeeklyUserCalendarProps {
  userData: any;
}

/** Pending change to be applied after version confirmation */
type PendingChange = PendingWeeklyWorkDayChange;

const WeeklyUserCalendar: React.FC<WeeklyUserCalendarProps> = ({ userData }) => {
  let eventGuid = 0;
  moment.locale("de");

  const [workPlanOutdated, setWorkPlanOutdated] = useState<boolean>(false);
  const [workPlanStatusLoading, setWorkPlanStatusLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Moments.Moment | null>(moment());
  const [endDate, setEndDate] = useState<Moments.Moment | null>(moment());
  const [range, setRange] = useState<Moments.Moment[] | null>();
  const formattedStartDate = startDate?.format("DD MMM YYYY");
  const formattedEndDate = endDate?.format("DD MMM YYYY");
  const [workPlanId, setWorkPlanId] = useState<number>();
  // Pending changes awaiting version confirmation (supports multiple edits)
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  // Controls whether the versioning dialog is shown
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  // Why the last versioning attempt was refused, shown inside the dialog.
  const [versionError, setVersionError] = useState<string | null>(null);
  
  // React Query hooks
  const { data: workPlans } = useWorkPlans(userData.id);
  const { data: weeklyWorkDays } = useWeeklyWorkDaysByWorkPlan(userData.id, workPlanId);

  /**
   * The selected plan, DERIVED from the query on every render — never a copy
   * held in state.
   *
   * Keeping the plan object in state meant it stayed on the values it had at
   * selection time. Editing the plan's validity dates in the table next to this
   * calendar refetches the query, but the copy kept the old dates, so
   * `hasHistoricalData` below still judged by them: a plan moved from the
   * future into the past saved straight through without ever offering the
   * versioning dialog, until the page was reloaded (MD-292).
   */
  const selectedWorkPlan = React.useMemo(
    () => workPlans?.find((plan) => plan.id === workPlanId) ?? null,
    [workPlans, workPlanId],
  );
  const createWeeklyWorkDayMutation = useCreateWeeklyWorkDay();
  const updateWeeklyWorkDayMutation = useUpdateWeeklyWorkDay();
  const versionWorkPlanMutation = useVersionWorkPlan();
  const { setUnsavedChanges, guard } = useUnsavedChanges();

  /**
   * Pending changes are unsaved work, so leaving this view with them asks
   * first — the same guard the Info tab's form uses for the sub-tab strip, the
   * close button and a browser reload. They used to vanish silently on any of
   * those, which is exactly the "the work is really gone" risk MD-463 named.
   *
   * "Speichern" cannot save in place: a plan with history needs an effective
   * date. It opens that dialog instead and returns false, which keeps the user
   * here to finish applying.
   */
  const hasPendingChanges = pendingChanges.length > 0;
  useEffect(() => {
    setUnsavedChanges(hasPendingChanges, () => {
      setShowVersionDialog(true);
      return false;
    });
    return () => setUnsavedChanges(false);
  }, [hasPendingChanges, setUnsavedChanges]);

  const createEventId = () => {
    return String(eventGuid++);
  };

  /**
   * Check if the selected work plan has historical data (validityStart < today).
   * If so, changes require versioning instead of direct editing.
   * Plans starting today have no history yet - the whole day is still in progress.
   * Note: validityStart may come formatted (DD.MM.YYYY) or as ISO string
   */
  const hasHistoricalData = (): boolean => {
    if (!selectedWorkPlan?.validityStart) return false;
    // Try explicit German format first, then fallback to ISO
    let start = moment(selectedWorkPlan.validityStart, "DD.MM.YYYY");
    if (!start.isValid()) start = moment(selectedWorkPlan.validityStart);
    // Only plans that started BEFORE today have history - today is still in progress
    return start.isValid() && start.isBefore(moment(), "day");
  };

  /**
   * An EXPIRED plan cannot be edited here at all.
   *
   * Versioning closes the edited plan by setting its end to the effective date
   * — on a plan whose end already passed that EXTENDS it, straight over
   * whatever plans followed it (MD-292). The API rejects it for that reason, so
   * the calendar must not collect changes it cannot apply. Its hours are
   * history; only its validity dates can still be corrected, in the table
   * beside the calendar.
   *
   * `validityEnd` is exclusive, so a plan ending today is already over — the
   * same rule the plan list uses to sort plans into active and expired.
   */
  const isExpiredPlan = (): boolean => {
    if (!selectedWorkPlan?.validityEnd) return false;
    const end = moment(selectedWorkPlan.validityEnd);
    return end.isValid() && end.isSameOrBefore(moment(), "day");
  };

  const expiredPlanMessage =
    "Dieser Arbeitsplan ist abgelaufen und kann in seinen Zeiten nicht mehr geändert " +
    "werden. Nur seine Gültigkeitsdaten lassen sich noch korrigieren.";

  // A work plan belongs to exactly one user. Clearing the selection when the
  // view switches user stops `handleCreateEvent`/`handleChangeEvent` from
  // posting `{ workPlan: <previous user's plan>, employeeId: <this user> }`.
  useEffect(() => {
    setWorkPlanId(undefined);
    setPendingChanges([]);
    setShowVersionDialog(false);
  }, [userData?.id]);

  /**
   * The week as it currently stands ON SCREEN: what is saved, with the pending
   * changes laid over it. The table beside the calendar builds its rows and its
   * hour totals from the same helper, so the two cannot show different weeks.
   */
  const mergedWorkDays = React.useMemo(
    () => mergePendingWorkDays(weeklyWorkDays, pendingChanges),
    [weeklyWorkDays, pendingChanges],
  );

  /**
   * Build events array from the merged work days.
   *
   * A pending row is drawn in a distinct colour and named "ausstehend": it is
   * on screen because the user just put it there, but it is not saved, and
   * nothing about a normally coloured block would say so.
   */
  const buildEventsFromWorkDays = (workDays: MergedWeeklyWorkDay[]): Event[] => {
    return workDays.map((workDay) => ({
      id: workDay.displayId,
      title: `${workDay.isBreak ? "Pause" : "Arbeitszeit"}${workDay.isPending ? " (ausstehend)" : ""}`,
      start: workDay.startTime!,
      end: workDay.endTime,
      startTime: formatWorkDayTime(workDay.startTime, "HH:mm:ss"),
      endTime: formatWorkDayTime(workDay.endTime, "HH:mm:ss"),
      daysOfWeek: [workDay.dayOfWeek?.toString()!],
      editable: true,
      color: workDay.isPending
        ? PENDING_COLOR
        : workDay.isBreak
          ? mobiliTheme.palette.info.light
          : mobiliTheme.palette.secondary.main,
    }));
  };

  /**
   * Derived, never held in state.
   *
   * It used to be state, refilled by an effect on the query — so every refetch
   * repainted the calendar from the SAVED week and silently threw away the
   * position the user had just dragged a block to. Deriving it means a repaint
   * can only ever redraw what is actually on screen, pending changes included.
   */
  const events = React.useMemo(
    () => buildEventsFromWorkDays(mergedWorkDays),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mergedWorkDays],
  );

  /**
   * Cancel pending changes. The calendar follows on its own, because it draws
   * from `mergedWorkDays`.
   */
  const handleCancelPendingChanges = () => {
    setPendingChanges([]);
  };

  const setRangeDates = () => {
    if (startDate && endDate) {
      const range = momentRange.range(startDate!, endDate!);
      const dates: any = Array.from(range.by("day"));
      setRange(dates);
    }
  };

  const handleDatesChange = ({
    startDate,
    endDate,
  }: {
    startDate: Moments.Moment | null;
    endDate: Moments.Moment | null;
  }) => {
    setStartDate(startDate);
    setEndDate(endDate);
    setRangeDates();
    // handleInputChange("from", startDate?.format("YYYY-MM-DD"));
    // handleInputChange("to", endDate?.format("YYYY-MM-DD"));
  };

  const selectWorkPlan = (workPlan: WorkPlanDto) => {
    setWorkPlanId(workPlan.id!);
    setPendingChanges([]);
    setShowVersionDialog(false);
  };

  /**
   * Switching to another plan drops the pending changes, so it goes through the
   * guard. Clicking the plan that is already open (e.g. its edit button, to
   * correct the validity dates) is not a switch and keeps them.
   */
  const handleWorkPlanClick = (workPlan: WorkPlanDto) => {
    if (workPlan.id === workPlanId) return;
    guard(() => selectWorkPlan(workPlan));
  };

  const handleWorkPlanDelete = (workPlan: WorkPlanDto) => {
    setWorkPlanId(undefined);
    setPendingChanges([]);
    setShowVersionDialog(false);
    // console.log("workPlan deleted", workPlan);
  };

  /**
   * Add a pending change. Does NOT open the dialog - user must click Apply in the banner.
   * If updating the same weeklyWorkDay id, replace the previous change.
   */
  const addPendingChange = (change: PendingChange) => {
    setPendingChanges((prev) => {
      // For updates, replace any existing change for the same id
      if (change.type === "update" && change.weeklyWorkDay.id) {
        const filtered = prev.filter(
          (c) => !(c.type === "update" && c.weeklyWorkDay.id === change.weeklyWorkDay.id)
        );
        return [...filtered, change];
      }
      // A pending row that is dragged again replaces itself — it is on screen
      // now, so a second move must move it, not add another block beside it.
      if (change.type === "create" && change.clientId) {
        const filtered = prev.filter((c) => c.clientId !== change.clientId);
        return [...filtered, change];
      }
      // For creates, just append
      return [...prev, change];
    });
    // Do NOT show the dialog immediately - user will click Apply in the banner
  };

  const handleCreateEvent = async (event: DateSelectArg) => {
    if (!workPlanId) {
      notificationStore.showMessage("Bitte wähle einen Arbeitsplan aus", "error");
      return;
    }
    // console.log("create event clicked", event);
    // Note: drawing on this weekly calendar always creates a WORK block —
    // work blocks are draggable foreground events, so a selection can never
    // start on top of one. Voluntary breaks (MD-238 P4) are created via the
    // "Typ: Pause" option in the WeeklyWorkDays table next to this calendar.
    const weeklyWorkDay: WeeklyWorkDayDto = {
      workPlan: { id: workPlanId },
      dayOfWeek: new Date(event.start).getDay(),
      startTime: new Date(event.start).toISOString(),
      endTime: new Date(event.end)?.toISOString(),
      employeeId: userData.id,
    };

    if (isExpiredPlan()) {
      notificationStore.showMessage(expiredPlanMessage, "error");
      return;
    }

    // If plan has historical data, show versioning dialog
    if (hasHistoricalData()) {
      addPendingChange({ type: "create", weeklyWorkDay, clientId: createPendingId() });
      return;
    }

    try {
      await createWeeklyWorkDayMutation.mutateAsync(weeklyWorkDay);
      // React Query automatically invalidates and refetches the data
    } catch (error) {
      console.error(error);
    }
  };

  const handleChangeEvent = async (event: Event, _relatedEvents?: Event[], revert?: () => void) => {
    if (!workPlanId) {
      notificationStore.showMessage("Bitte wähle einen Arbeitsplan aus", "error");
      revert?.();
      return;
    }

    // The block may itself be a pending one the user drew a moment ago; it has
    // no database id, only the handle it was given when it was added.
    const pendingClientId = isPendingId(event.id) ? event.id! : undefined;
    const movedRow = mergedWorkDays.find((row) => row.displayId === event.id);
    const isBreak = movedRow?.isBreak ?? false;

    // Extract day of week and times from the event
    const startDate = new Date(event.start);
    const endDate = new Date(event.end!);

    // For recurring weekly events, we need to store the time portion only
    // Create ISO strings with times on a reference date
    const weeklyWorkDay: WeeklyWorkDayDto = {
      id: pendingClientId ? undefined : parseInt(event.id!),
      workPlan: { id: workPlanId },
      dayOfWeek: startDate.getDay(),
      startTime: startDate.toISOString(),
      endTime: endDate.toISOString(),
      employeeId: userData.id,
      isBreak,
    };

    if (isExpiredPlan()) {
      notificationStore.showMessage(expiredPlanMessage, "error");
      revert?.();
      return;
    }

    // Moving a block that is not saved yet stays a pending CREATE — there is
    // nothing on the server to update.
    if (pendingClientId) {
      addPendingChange({ type: "create", weeklyWorkDay, clientId: pendingClientId });
      return;
    }

    // If plan has historical data AND it's not a break, show versioning dialog
    // Breaks use the exception system instead of versioning
    if (hasHistoricalData() && !isBreak) {
      addPendingChange({ type: "update", weeklyWorkDay });
      return;
    }

    // console.log("update weeklyWorkDay", weeklyWorkDay);
    try {
      await updateWeeklyWorkDayMutation.mutateAsync(weeklyWorkDay);
      // React Query automatically invalidates and refetches the data
    } catch (error) {
      // Rejected save (e.g. the MD-286 cross-site hour guard): the query
      // cache never changed, so no refetch will repaint the calendar — undo
      // FullCalendar's optimistic move explicitly or the user keeps seeing
      // an unsaved position.
      revert?.();
      console.error("update error", error);
    }
  };

  /**
   * Handle version confirmation - create a new work plan version with all pending changes applied.
   */
  const handleVersionConfirm = async (effectiveDate: string) => {
    if (!workPlanId || pendingChanges.length === 0 || !weeklyWorkDays) {
      return;
    }

    setVersionError(null);

    try {
      // Build the new weekly work days list with all pending changes applied
      let newWeeklyWorkDays: WeeklyWorkDayDto[] = [...weeklyWorkDays];

      for (const change of pendingChanges) {
        if (change.type === "create") {
          // Add the new weekly work day
          newWeeklyWorkDays = [...newWeeklyWorkDays, change.weeklyWorkDay];
        } else {
          // Update: replace the existing weekly work day with the changed one
          newWeeklyWorkDays = newWeeklyWorkDays.map((wwd) =>
            wwd.id === change.weeklyWorkDay.id ? change.weeklyWorkDay : wwd
          );
        }
      }

      const response = await versionWorkPlanMutation.mutateAsync({
        workPlanId,
        effectiveDate,
        weeklyWorkDays: newWeeklyWorkDays,
        employeeId: userData.id,
      });

      setPendingChanges([]);
      setShowVersionDialog(false);

      // Auto-select the newly created work plan. Not through the guard: the
      // changes were just applied, and the cleared state has not re-rendered yet.
      selectWorkPlan(response as WorkPlanDto);
    } catch (error: any) {
      console.error("Error versioning work plan:", error);
      notificationStore.showMessage(
        error?.response?.data?.message || "Fehler beim Erstellen der neuen Version",
        "error"
      );
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <Grid container spacing={2}>
          {/* Arbeitsplan gets a row of its own. Squeezed into a third of the
              width, its table needed an internal horizontal scroll to reach
              the action buttons. */}
          <Grid item xs={12} sx={{ textAlign: "left" }}>
            <WorkPlans
              user={userData}
              selectedWorkPlanId={workPlanId}
              onItemClick={handleWorkPlanClick}
              onDeleted={handleWorkPlanDelete}
            />
          </Grid>
          <Grid item xs={12} sx={{ textAlign: "left" }}>
            {workPlanId && (
              <>
                <WeeklyWorkDays
                  user={userData}
                  workPlanId={workPlanId}
                  hasHistoricalData={hasHistoricalData()}
                  readOnly={isExpiredPlan()}
                  pendingChanges={pendingChanges}
                  onVersioningRequired={(change) => {
                    addPendingChange(change);
                  }}
                />
              </>
            )}
          </Grid>
        </Grid>
      </Box>
      {workPlanId && (
        <TopLevelPaper>
          {/* Banner showing pending changes above the calendar */}
          <PendingChangeBanner
            pendingChanges={pendingChanges}
            onCancel={handleCancelPendingChanges}
            onApply={() => setShowVersionDialog(true)}
          />
          {/* Say WHY nothing can be dragged here, so an inert calendar does not
              read as a defect. */}
          {isExpiredPlan() && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Abgelaufener Arbeitsplan — nur zur Ansicht. Die Zeiten dieses Plans sind
              Vergangenheit und können nicht mehr geändert werden.
            </Alert>
          )}
          <WeeklyCalendar
            events={events}
            readOnly={isExpiredPlan()}
            // onDatesSet={() => {}}
            onCreateEvent={handleCreateEvent}
            onChangeEvent={handleChangeEvent}
            // onRemoveEvent={() => {}}
            // onOutdated={() => {}}}       
               />
        </TopLevelPaper>
      )}
      <EffectiveDateDialog
        open={showVersionDialog}
        // Cancelling the date dialog only backs out of APPLYING. It used to
        // discard every pending change as well — one misclick lost all of them.
        // Discarding is VERWERFEN in the banner.
        onClose={() => setShowVersionDialog(false)}
        onContinueEditing={() => setShowVersionDialog(false)}
        onConfirm={handleVersionConfirm}
        workPlanId={workPlanId!}
        originalValidityStart={selectedWorkPlan?.validityStart?.toString()}
        pendingChanges={pendingChanges}
        loading={versionWorkPlanMutation.isPending}
      />
    </>
  );
};

export default WeeklyUserCalendar;
